import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, MapPin, Clock, CheckCircle, Star, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';

const CityPageTemplate = ({ cityData }) => {
  const {
    cityName,
    metaDescription,
    heroTitle,
    heroSubtitle,
    introduction,
    services,
    whyChooseUs,
    serviceAreas,
    testimonial
  } = cityData;

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-navy py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <p className="text-[#00AEEF] font-manrope mb-4">Trusted Local Plumber</p>
            <h1 className="font-oswald text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {heroTitle}
            </h1>
            <p className="font-manrope text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              {heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="btn-primary text-lg px-8 py-4">
                  Get a Free Quote
                </Button>
              </Link>
              <a href="tel:651-738-9354">
                <Button className="bg-white text-[#14366C] hover:bg-gray-100 font-oswald font-bold uppercase tracking-wider px-8 py-4 text-lg">
                  <Phone className="w-5 h-5 mr-2" />
                  651-738-9354
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-oswald text-3xl md:text-4xl font-bold text-[#14366C] mb-6">
                Professional Plumbing Services in {cityName}, MN
              </h2>
              <div className="prose prose-lg max-w-none text-gray-700">
                <p className="mb-4">{introduction}</p>
                <p>
                  As a locally owned and operated business since 1976, we understand the unique plumbing 
                  needs of {cityName} homeowners and businesses. Whether you need repairs, 
                  routine maintenance, or a complete plumbing installation, our licensed technicians 
                  are ready to help.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-[#F5F0EB]">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-oswald text-3xl md:text-4xl font-bold text-[#14366C] mb-4">
              Our Plumbing Services in {cityName}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We offer comprehensive plumbing and heating services for residential and commercial properties.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <h3 className="font-oswald text-xl font-bold text-[#14366C] mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <Link 
                  to={service.link} 
                  className="text-[#F58220] font-semibold hover:underline inline-flex items-center"
                >
                  Learn More <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-oswald text-3xl md:text-4xl font-bold text-[#14366C] mb-4">
                Why {cityName} Residents Choose BNPH
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {whyChooseUs.map((item, index) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-4"
                >
                  <CheckCircle className="w-6 h-6 text-[#F58220] flex-shrink-0 mt-1" />
                  <p className="text-gray-700 text-lg">{item}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      {testimonial && (
        <section className="py-16 bg-[#14366C]">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 text-[#F58220] fill-[#F58220]" />
                ))}
              </div>
              <blockquote className="text-xl md:text-2xl text-white italic mb-6">
                "{testimonial.text}"
              </blockquote>
              <p className="text-gray-300">
                — {testimonial.author}, {cityName} Resident
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* Service Areas Section */}
      <section className="py-16 bg-[#F5F0EB]">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="font-oswald text-3xl font-bold text-[#14366C] mb-4">
              Serving {cityName} & Surrounding Areas
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We proudly serve {cityName} and the entire Twin Cities metro area.
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {serviceAreas.map((area) => (
              <Link
                key={area.name}
                to={area.link}
                className="bg-white px-4 py-2 rounded-full text-[#14366C] hover:bg-[#14366C] hover:text-white transition-colors shadow-sm"
              >
                {area.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#14366C]">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-oswald text-3xl md:text-4xl font-bold text-white mb-6">
              Need a Plumber in {cityName}?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Contact Bruce Nelson Plumbing & Heating today for fast, reliable service. 
              We're available Monday through Friday, 7AM to 4PM.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="btn-primary text-lg px-8 py-4">
                  Contact Us
                </Button>
              </Link>
              <a href="tel:651-738-9354">
                <Button className="bg-white text-[#14366C] hover:bg-gray-100 font-oswald font-bold uppercase tracking-wider px-8 py-4 text-lg">
                  <Phone className="w-5 h-5 mr-2" />
                  651-738-9354
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Local Info Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row justify-center items-center gap-8 text-center md:text-left">
            <div className="flex items-center gap-3">
              <MapPin className="w-6 h-6 text-[#F58220]" />
              <div>
                <p className="font-bold text-[#14366C]">Located in Oakdale</p>
                <p className="text-gray-600">7012 6th St. N., Oakdale, MN 55128</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-[#F58220]" />
              <div>
                <p className="font-bold text-[#14366C]">Business Hours</p>
                <p className="text-gray-600">Mon-Fri: 7AM - 4PM</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-6 h-6 text-[#F58220]" />
              <div>
                <p className="font-bold text-[#14366C]">Call Us</p>
                <a href="tel:651-738-9354" className="text-[#F58220] hover:underline">651-738-9354</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityPageTemplate;
